import React, { useState, useEffect } from 'react';
import { Search, ImageOff, Loader2 } from 'lucide-react';
import { scanBook, getMarketplaceListings } from '../services/api';
import { uploadToCloudinary } from '../services/cloudinary';

export interface ListingFormData {
  id?: string;
  title: string;
  author?: string;
  condition: string;
  price_lkr: number;
  stock_quantity: number;
  description: string;
  city: string;
  photos: string[];
}

interface ListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: ListingFormData | null;
  onSave: (data: ListingFormData, isDraft: boolean) => Promise<void>;
  showToast: (msg: string) => void;
}

export const ListingModal: React.FC<ListingModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onSave,
  showToast,
}) => {
  const [formData, setFormData] = useState<ListingFormData>({
    title: '',
    author: '',
    condition: 'new',
    price_lkr: 0,
    stock_quantity: 1,
    description: '',
    city: '',
    photos: [],
  });

  const [isbn, setIsbn] = useState('');
  const [isbnLoading, setIsbnLoading] = useState(false);
  
  const [avgPrice, setAvgPrice] = useState<number | null>(null);
  const [priceLoading, setPriceLoading] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData(initialData);
        if (initialData.title) fetchMarketPriceHint(initialData.title);
      } else {
        setFormData({
          title: '',
          author: '',
          condition: 'new',
          price_lkr: 0,
          stock_quantity: 1,
          description: '',
          city: '',
          photos: [],
        });
        setAvgPrice(null);
      }
      setIsbn('');
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const fetchMarketPriceHint = async (titleQuery: string) => {
    if (!titleQuery.trim()) return;
    setPriceLoading(true);
    try {
      const listings = await getMarketplaceListings();
      const matched = listings.filter((l: any) => 
        l.title.toLowerCase().includes(titleQuery.toLowerCase())
      );
      if (matched.length > 0) {
        const sum = matched.reduce((acc: number, curr: any) => acc + curr.price_lkr, 0);
        setAvgPrice(Math.round(sum / matched.length));
      } else {
        setAvgPrice(null);
      }
    } catch {
      setAvgPrice(null);
    } finally {
      setPriceLoading(false);
    }
  };

  const handleTitleBlur = () => {
    if (formData.title && !initialData) {
      fetchMarketPriceHint(formData.title);
    }
  };

  const handleIsbnScan = async () => {
    if (!isbn.trim()) return;
    setIsbnLoading(true);
    try {
      const book = await scanBook(isbn);
      if (book) {
        setFormData(prev => ({
          ...prev,
          title: book.title || prev.title,
          author: book.author || prev.author,
          photos: book.cover_url ? [book.cover_url] : prev.photos,
        }));
        showToast("Book details filled!");
        if (book.title) fetchMarketPriceHint(book.title);
      } else {
        showToast("Book not found. Fill manually.");
      }
    } catch {
      showToast("Book not found. Fill manually.");
    } finally {
      setIsbnLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    try {
      const url = await uploadToCloudinary(file, setUploadProgress);
      setFormData(prev => ({ ...prev, photos: [url] }));
    } catch (err) {
      showToast("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent, isDraft: boolean) => {
    e.preventDefault();
    if (!formData.title || formData.price_lkr <= 0 || !formData.condition) {
      showToast("Please fill in all required fields.");
      return;
    }
    setSaving(true);
    try {
      await onSave(formData, isDraft);
    } finally {
      setSaving(false);
    }
  };

  const matchLowestPrice = () => {
    if (avgPrice) {
      setFormData(prev => ({ ...prev, price_lkr: avgPrice }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-[#F1EFE8] rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slide-up">
        <div className="sticky top-0 bg-[#F1EFE8] px-6 py-4 flex items-center justify-between border-b border-gray-200 z-10">
          <h2 className="text-xl font-bold text-[#085041]">
            {initialData ? 'Edit Listing' : 'Add New Listing'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500">✕</button>
        </div>

        <form className="p-6 space-y-6">
          {/* Cover Image Upload */}
          <div className="flex flex-col items-center">
            <label className="cursor-pointer group relative flex flex-col items-center justify-center w-32 h-44 rounded-lg border-2 border-dashed border-[#1D9E75] bg-white overflow-hidden hover:bg-gray-50 transition-colors">
              {formData.photos?.[0] ? (
                <img src={formData.photos[0]} alt="Cover" className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center text-[#1D9E75]">
                  <ImageOff size={24} className="mb-2" />
                  <span className="text-xs font-semibold">Upload Cover</span>
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white">
                  <Loader2 size={24} className="animate-spin mb-1" />
                  <span className="text-xs font-semibold">{uploadProgress}%</span>
                </div>
              )}
              <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Book Title *</label>
              <input 
                type="text" required
                value={formData.title} 
                onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                onBlur={handleTitleBlur}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#085041] focus:border-transparent outline-none" 
              />
            </div>
            
            {/* Author */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Author</label>
              <input 
                type="text" 
                value={formData.author || ''} 
                onChange={e => setFormData(p => ({ ...p, author: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#085041] focus:border-transparent outline-none" 
              />
            </div>

            {/* ISBN */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">ISBN Auto-fill</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={isbn}
                  onChange={e => setIsbn(e.target.value)}
                  placeholder="Enter 10 or 13 digit ISBN"
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#085041] focus:border-transparent outline-none" 
                />
                <button 
                  type="button"
                  onClick={handleIsbnScan}
                  disabled={isbnLoading || !isbn}
                  className="px-4 py-2 bg-[#085041] text-white rounded-lg flex items-center justify-center hover:bg-teal-900 disabled:opacity-50 transition-colors shadow-sm"
                >
                  {isbnLoading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                </button>
              </div>
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Price (Rs.) *</label>
              <input 
                type="number" required min="0" step="1"
                value={formData.price_lkr || ''} 
                onChange={e => setFormData(p => ({ ...p, price_lkr: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#085041] focus:border-transparent outline-none" 
              />
            </div>

            {/* Market Price Hint */}
            <div className="flex items-end pb-1 h-[68px]">
              {priceLoading ? (
                <div className="flex items-center text-sm text-gray-500 gap-2 h-[42px]">
                  <Loader2 size={16} className="animate-spin" /> Calculating avg price...
                </div>
              ) : avgPrice ? (
                <div className="flex w-full items-center justify-between text-sm text-[#BA7517] h-[42px] bg-amber-50 px-3 rounded-lg border border-amber-200">
                  <span className="flex items-center font-medium">💡 Avg price: Rs. {avgPrice}</span>
                  <button type="button" onClick={matchLowestPrice} className="text-xs bg-white border border-[#BA7517] text-[#BA7517] px-2 py-1 rounded shadow-sm hover:bg-amber-100 font-bold ml-1">
                    Match
                  </button>
                </div>
              ) : null}
            </div>

            {/* Condition */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Condition *</label>
              <select 
                value={formData.condition}
                onChange={e => setFormData(p => ({ ...p, condition: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#085041] focus:border-transparent outline-none bg-white"
              >
                <option value="new">New</option>
                <option value="like_new">Like New</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
              </select>
            </div>

            {/* Stock */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Stock Qty *</label>
              <div className="flex items-center">
                <button type="button" onClick={() => setFormData(p => ({...p, stock_quantity: Math.max(1, p.stock_quantity - 1)}))} className="px-3 py-2 bg-gray-200 active:bg-gray-300 rounded-l-lg border-y border-l border-gray-300 font-bold text-gray-700 transition-colors">-</button>
                <input 
                  type="number" required min="1"
                  value={formData.stock_quantity} 
                  onChange={e => setFormData(p => ({ ...p, stock_quantity: Math.max(1, parseInt(e.target.value) || 1) }))}
                  className="w-16 px-2 py-2 text-center border-y border-gray-300 focus:ring-0 focus:outline-none" 
                />
                <button type="button" onClick={() => setFormData(p => ({...p, stock_quantity: p.stock_quantity + 1}))} className="px-3 py-2 bg-gray-200 active:bg-gray-300 rounded-r-lg border-y border-r border-gray-300 font-bold text-gray-700 transition-colors">+</button>
              </div>
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              <textarea 
                rows={3}
                value={formData.description || ''}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#085041] focus:border-transparent outline-none resize-y" 
              />
            </div>

            {/* City */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">City</label>
              <input 
                type="text" 
                value={formData.city || ''} 
                onChange={e => setFormData(p => ({ ...p, city: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#085041] focus:border-transparent outline-none" 
              />
            </div>

          </div>
          
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 mt-6">
            <button
              type="button"
              disabled={saving}
              onClick={(e) => handleSubmit(e, true)}
              className="px-5 py-2 text-[#085041] font-semibold hover:bg-teal-50 rounded-lg transition-colors border border-[#085041] shadow-sm flex items-center"
            >
              Save as Draft
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={(e) => handleSubmit(e, false)}
              className="px-5 py-2 bg-[#085041] text-white font-semibold flex items-center justify-center rounded-lg hover:bg-teal-900 transition-colors shadow-sm disabled:opacity-50"
            >
              {saving && <Loader2 size={16} className="animate-spin mr-2" />}
              Publish Listing
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
